/**
 * Helper utilities
 */

/**
 * Populate a select element with options
 */
export function populateSelect<T>(
  select: HTMLSelectElement,
  items: T[],
  valueKey: keyof T,
  textKey: keyof T,
  placeholder?: string
): void {
  // Clear existing options
  select.innerHTML = '';

  // Add placeholder if provided
  if (placeholder) {
    const option = document.createElement('option');
    option.value = '';
    option.textContent = placeholder;
    option.disabled = true;
    option.selected = true;
    select.appendChild(option);
  }

  // Add items
  for (const item of items) {
    const option = document.createElement('option');
    option.value = String(item[valueKey]);
    option.textContent = String(item[textKey]);
    select.appendChild(option);
  }
}

/**
 * Show a status message
 */
export function showMessage(
  element: HTMLElement,
  message: string,
  type: 'success' | 'error' | 'info' | 'warning' = 'info',
  isHtml = false
): void {
  if (isHtml) {
    element.innerHTML = message;
  } else {
    element.textContent = message;
  }
  element.className = `message message-${type}`;
  element.style.display = 'block';
}

/**
 * Hide a status message
 */
export function hideMessage(element: HTMLElement): void {
  element.style.display = 'none';
  element.textContent = '';
}

/**
 * Set loading state on a button
 */
export function setButtonLoading(
  button: HTMLButtonElement,
  loading: boolean,
  loadingText?: string
): void {
  if (loading) {
    button.disabled = true;
    button.dataset.originalText = button.textContent || '';
    button.textContent = loadingText || 'Loading...';
  } else {
    button.disabled = false;
    button.textContent = button.dataset.originalText || button.textContent;
  }
}

/**
 * Format a person's name
 */
export function formatPersonName(firstName: string, lastName: string): string {
  return `${firstName} ${lastName}`.trim();
}

/**
 * Escape HTML entities
 */
export function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Debounce function calls
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return function (this: unknown, ...args: Parameters<T>) {
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(() => {
      func.apply(this, args);
    }, wait);
  };
}

/**
 * Wait for Office.js to be ready
 */
export function waitForOffice(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof Office !== 'undefined' && Office.context) {
      resolve();
    } else {
      Office.onReady(() => {
        resolve();
      });
    }
  });
}

/**
 * Navigate to settings page
 */
export function goToSettings(): void {
  window.location.href = 'settings.html';
}

/**
 * Navigate to create ticket page
 */
export function goToCreateTicket(): void {
  window.location.href = 'createticket.html';
}

/**
 * Navigate to add to log page
 */
export function goToAddToLog(): void {
  window.location.href = 'addtolog.html';
}

/**
 * Truncate text with ellipsis
 */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - 3) + '...';
}

/**
 * Get element by ID with type assertion
 */
export function getElementById<T extends HTMLElement>(id: string): T | null {
  return document.getElementById(id) as T | null;
}

/**
 * Add event listener with null check
 */
export function addClickListener(
  elementId: string,
  handler: (e: Event) => void
): void {
  const element = document.getElementById(elementId);
  if (element) {
    element.addEventListener('click', handler);
  }
}
